// web/src/components/AppShell.tsx — portal frame: sidebar ≥768px ↔ bottom tab bar <768px (D07).
// Workspace name top-left; user menu with Log out (MSAL, D14 §1); admin nav appears only when me.isAdmin (D15).
import { useEffect, useState } from 'react';
import type { ReactNode } from 'react';
import { NavLink } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api';
import { logout } from '../lib/auth';
import { BRAND } from '../lib/brand';
import { Icon } from './Icon';
import { INVITE_KEY } from '../screens/Invite';
import { NoAccess } from '../screens/NoAccess';
import { Preview } from '../screens/Preview';
import { toast } from './Toast';
import { Skeleton } from './Skeleton';
import { ADMIN_NAV, useAdminCounts, type BadgeKey } from '../admin/adminNav';
import { CountPill } from '../admin/adminUi';

interface NavItem { to: string; label: string; icon: string; badge?: BadgeKey }

// Bottom tab bar (≤5 thumb destinations) and the fuller desktop sidebar. Portal spec 17: Reports took Settings' tab;
// Settings lives in the account menu on phones.
const PRIMARY: NavItem[] = [
  { to: '/', label: 'Home', icon: 'home' },
  { to: '/site', label: 'My Site', icon: 'world' },
  { to: '/stripe', label: 'Payments', icon: 'credit-card' },
  { to: '/requests', label: 'Requests', icon: 'list' },
  { to: '/reports', label: 'Reports', icon: 'bar-chart' },
];
const SIDEBAR: NavItem[] = [
  { to: '/', label: 'Home', icon: 'home' },
  { to: '/site', label: 'My Site', icon: 'world' },
  { to: '/stripe', label: 'Payments', icon: 'credit-card' },
  { to: '/requests', label: 'Requests', icon: 'list' },
  { to: '/reports', label: 'Reports', icon: 'bar-chart' },
  { to: '/photo', label: 'Send photos', icon: 'image' },
  { to: '/billing', label: 'Billing', icon: 'wallet' },
  { to: '/support', label: 'Support', icon: 'headset' },
  { to: '/settings', label: 'Settings', icon: 'settings' },
];
// The operator nav (portal spec 22 §4d) lives in admin/adminNav.tsx: the desktop sidebar below and the phone strip at
// the top of every admin page (AdminGate) read the same list and the same badge counts.


export function AppShell({ children }: { children: ReactNode }) {
  const me = useQuery({ queryKey: ['me'], queryFn: () => api.me() });
  const ws = me.data?.orgName ?? 'Workspace';
  const isAdmin = me.data?.isAdmin ?? false;
  const [menu, setMenu] = useState(false);
  const counts = useAdminCounts(isAdmin).data; // nav badges (portal spec 22), polled every 60 s
  // Portal spec 16: Payments shows only when one of the user's sites takes payments online.
  const sites = useQuery({ queryKey: ['sites'], queryFn: () => api.listSites(), enabled: !!me.data && me.data.access !== 'none' });
  const showPayments = (sites.data ?? []).some((s) => s.takesPayments);
  const SIDEBAR_ITEMS = SIDEBAR.filter((n) => n.to !== '/stripe' || showPayments);
  const PRIMARY_ITEMS = PRIMARY.filter((n) => n.to !== '/stripe' || showPayments);

  // Portal spec 13: accept an invite token stashed by /invite/:token, then gate on access.
  const qc = useQueryClient();
  const [accepting, setAccepting] = useState(() => !!sessionStorage.getItem(INVITE_KEY));
  useEffect(() => {
    const t = sessionStorage.getItem(INVITE_KEY);
    if (!t) return;
    sessionStorage.removeItem(INVITE_KEY);
    api.acceptInvite(t)
      .then(() => toast({ kind: 'success', message: 'Your dashboard is ready.' }))
      .catch((e: unknown) => {
        const m = e instanceof Error ? e.message : '';
        toast({ kind: 'error', message: /different account/.test(m)
          ? 'That invite is for a different account. Sign out and use the email it was sent to.'
          : /Admins can/.test(m) ? 'That link is for a client. Open it with their login, not the admin one.'
          : 'That invite link has expired or was already used. Ask us for a new one.' });
      })
      .finally(() => { setAccepting(false); void qc.invalidateQueries(); });
  }, [qc]);

  const navItem = (n: NavItem, end = false) => (
    <NavLink key={n.to} to={n.to} end={end}
      className={({ isActive }) => `flex min-h-11 items-center gap-3 rounded-md px-3 text-sm ${isActive ? 'bg-chrome-surface text-chrome-strong' : 'text-chrome-text hover:bg-white/5 hover:text-chrome-strong'}`}>
      <Icon name={n.icon} size={18} />{n.label}
      {n.badge && <CountPill n={counts?.[n.badge]} />}
    </NavLink>
  );

  if (accepting || me.isLoading) {
    return <div className="grid min-h-dvh place-items-center bg-bg px-4"><div className="w-full max-w-md"><Skeleton variant="card" count={1} /></div></div>;
  }
  if (me.data?.access === 'none') return <div className="min-h-dvh bg-bg text-ink"><NoAccess email={me.data.email} /></div>;
  // Portal spec 24: a prospect's whole dashboard is the free-preview screen (the form, then their demo).
  if (me.data?.access === 'prospect') return <div className="min-h-dvh bg-bg text-ink"><Preview email={me.data.email} /></div>;

  return (
    <div className="min-h-dvh bg-bg text-ink">
      <a href="#content" className="sr-only focus:not-sr-only focus:absolute focus:left-2 focus:top-2 focus:z-50 focus:rounded focus:bg-surface focus:px-3 focus:py-2">Skip to content</a>

      {/* Desktop sidebar */}
      <aside className="fixed inset-y-0 left-0 hidden w-60 flex-col border-r border-chrome-border bg-chrome p-4 md:flex">
        <div className="mb-6 flex items-center gap-2 px-2">
          <span className="flex size-8 items-center justify-center rounded-md bg-run text-runink"><Icon name="rocket" size={18} /></span>
          <span className="font-head text-sm font-bold text-chrome-strong">{BRAND.shortName}</span>
        </div>
        <div className="mb-3 flex items-center gap-2 rounded-md border border-chrome-border bg-white/5 px-3 py-2">
          <Icon name="building" size={16} className="text-chrome-text" />
          <span className="truncate text-sm text-chrome-strong">{ws}</span>
        </div>
        <nav className="flex flex-col gap-1" aria-label="Primary">
          {SIDEBAR_ITEMS.map((n) => navItem(n, n.to === '/'))}
          {/* School link removed 2026-08-18 — product parked (marketing/plan/08). */}
        </nav>
        {isAdmin && (
          <nav className="mt-4 flex flex-col gap-1 border-t border-chrome-border pt-4" aria-label="Admin">
            <span className="px-3 pb-1 font-head text-[11px] uppercase text-chrome-text">Operator</span>
            {ADMIN_NAV.map((n) => navItem(n, n.to === '/admin'))}
          </nav>
        )}
        <div className="mt-auto flex items-center justify-between gap-2 px-2 pt-4">
          <span className="truncate text-xs text-chrome-text">{me.data?.email}</span>
          <button onClick={() => logout()} aria-label="Log out" title="Log out"
            className="flex size-8 shrink-0 items-center justify-center rounded-md text-chrome-text hover:bg-white/5 hover:text-chrome-strong">
            <Icon name="logout" size={16} />
          </button>
        </div>
      </aside>

      {/* Main column */}
      <div className="md:pl-60">
        {/* Mobile top bar with user menu */}
        <header className="sticky top-0 z-20 flex items-center justify-between border-b border-hair bg-bg/90 px-4 py-3 backdrop-blur md:hidden">
          <div className="flex items-center gap-2">
            <span className="flex size-7 items-center justify-center rounded-md bg-run text-runink"><Icon name="rocket" size={15} /></span>
            <span className="font-head text-sm text-ink">{BRAND.shortName}</span>
          </div>
          <div className="relative">
            <button onClick={() => setMenu((v) => !v)} aria-label="Account menu"
              className="flex items-center gap-1.5 rounded-md border border-hair px-2.5 py-1.5 text-xs text-muted">
              <Icon name="building" size={14} /><span className="max-w-[8rem] truncate">{ws}</span>
            </button>
            {menu && (
              <div className="absolute right-0 top-full z-30 mt-2 w-44 rounded-lg border border-hair bg-surface p-1 shadow-lg">
                <NavLink to="/settings" onClick={() => setMenu(false)} className="flex min-h-11 items-center gap-2 rounded-md px-3 py-2 text-sm text-ink hover:bg-surface2"><Icon name="settings" size={15} />Settings</NavLink>
                <NavLink to="/billing" onClick={() => setMenu(false)} className="flex min-h-11 items-center gap-2 rounded-md px-3 py-2 text-sm text-ink hover:bg-surface2"><Icon name="wallet" size={15} />Billing</NavLink>
                <NavLink to="/support" onClick={() => setMenu(false)} className="flex min-h-11 items-center gap-2 rounded-md px-3 py-2 text-sm text-ink hover:bg-surface2"><Icon name="headset" size={15} />Support</NavLink>
                {isAdmin && <NavLink to="/admin" onClick={() => setMenu(false)} className="flex min-h-11 items-center gap-2 rounded-md px-3 py-2 text-sm text-ink hover:bg-surface2"><Icon name="shield" size={15} />Admin<CountPill n={counts ? counts.leads + counts.lifecycle + counts.outbox + counts.review : 0} /></NavLink>}
                <button onClick={() => { setMenu(false); logout(); }} className="flex min-h-11 w-full items-center gap-2 rounded-md px-3 py-2 text-left text-sm text-ink hover:bg-surface2"><Icon name="logout" size={15} />Log out</button>
              </div>
            )}
          </div>
        </header>

        <main id="content" className="mx-auto w-full max-w-4xl px-4 pb-[calc(env(safe-area-inset-bottom)+5.5rem)] pt-5 md:pb-10">
          {children}
        </main>
      </div>

      {/* Mobile bottom tab bar (≤5 items, thumb zone, safe-area) */}
      <nav aria-label="Primary" className="fixed inset-x-0 bottom-0 z-30 flex border-t border-chrome-border bg-chrome pb-[env(safe-area-inset-bottom)] backdrop-blur md:hidden">
        {PRIMARY_ITEMS.map((n) => (
          <NavLink key={n.to} to={n.to} end={n.to === '/'}
            className={({ isActive }) => `flex flex-1 flex-col items-center gap-1 py-2 text-[11px] ${isActive ? 'text-run' : 'text-chrome-text'}`}>
            <Icon name={n.icon} size={22} />{n.label}
          </NavLink>
        ))}
      </nav>
    </div>
  );
}
